import { useState, useRef, useEffect } from 'react';
import { Scrollbar, Mousewheel, FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import styles from '@features/showrooms/sections/ShowroomsSection/Components/ShowroomInfo/ShowroomInfo.styles';
import Heading from '@components/Heading/Heading';
import ShowroomGallery from '@features/showrooms/sections/ShowroomsSection/Components/ShowroomGallery/ShowroomGallery';
import { IPointOfPresence, IPointOfPresenceWithDetails, IPointOfPresenceGalleryItem, IUserLocation } from '@features/checkout/typescript';
import nextShowroomApi from '@features/api/nextApiService/nextShowroomApi';
import Button from '@components/Button/Button';
import sanitizeHtml from '@utils/sanitizeHtml';
import { getShowroomName, prepareShowroomUrl, getPimInternalId, onClickNavigate, getNavigateUrl, navigationPossible, handleEmptyGallery, getShowroomReviewBoosterURL } from '@utils/showroom';
import Icon from '@components/icons/Icon';
type PropsType = {
  showroom: IPointOfPresence;
  setShowroomInfo: (newShowroomUuid: string | null, newShowroomInfo: IPointOfPresence | IPointOfPresenceWithDetails | null) => void;
  handleShowroomPage: () => void;
  userLocation: IUserLocation;
};
const ShowroomInfo = ({
  showroom,
  setShowroomInfo,
  handleShowroomPage,
  userLocation
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const swiperInstance = useRef<SwiperClass>(null);
  const [smallHeight, setSmallHeight] = useState<boolean>(true);
  const [scrollIsBeginning, setScrollIsBeginning] = useState<boolean>(true);
  const [reviewBoosterURL, setReviewBoosterURL] = useState<string | undefined>(undefined);
  const [activeSwiper, setActiveSwiper] = useState<boolean | undefined>(undefined);
  const CONFIG_SANITIZE = {
    allowedTags: ['strong', 'a', 'br'],
    allowedAttributes: {
      a: ['href', 'target', 'rel']
    }
  };
  const fetchShowroomDetails = async () => {
    setActiveSwiper(false);
    const result = showroom?.uuid ? await nextShowroomApi.getShowroom(showroom.uuid) : null;
    if (result) {
      if (result) result.gallery = handleEmptyGallery(result.gallery);
      setShowroomInfo(null, result);
      setActiveSwiper(true);
    }
  };
  const getDescriptionSummary = (): {
    __html: string;
  } => {
    const showroomDetails = showroom as IPointOfPresenceWithDetails;
    return sanitizeHtml(showroomDetails?.description?.summary ? showroomDetails.description.summary : '', CONFIG_SANITIZE);
  };
  const onScroll = event => {
    if (smallHeight) {
      setScrollIsBeginning(event.isBeginning);
    } else if (!scrollIsBeginning) {
      setScrollIsBeginning(true);
    }
  };
  const onResize = () => {
    if (window.innerHeight < 660) {
      setSmallHeight(true);
    } else {
      setSmallHeight(false);
    }
  };
  const onClickCall = () => {
    if (showroom?.contact?.phones[0]) {
      document.location.href = `tel:${showroom?.contact?.phones[0].replaceAll(' ', '')}`;
    }
  };
  const renderReviewBoosterURL = () => reviewBoosterURL ? <Button variant="outlined" variantColor="dark" size="small" href={reviewBoosterURL} openInNewTab>
        {t('button.showroomReviewBoosterURL')}
        <div>
          <Icon icon="Star" />
          <Icon icon="Star" />
          <Icon icon="Star" />
          <Icon icon="Star" />
          <Icon icon="Star" />
        </div>
      </Button> : null;
  useEffect(() => {
    if (!('gallery' in showroom) && !('contact' in showroom)) {
      fetchShowroomDetails();
    }
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
    };
  });
  useEffect(() => {
    const newReviewBoosterURL = getShowroomReviewBoosterURL(showroom);
    if (newReviewBoosterURL) {
      setReviewBoosterURL(newReviewBoosterURL);
    } else {
      setReviewBoosterURL(undefined);
    }
  }, [showroom?.ids?.['REVIEW-BOOSTER-URL']]);
  return <div sx={styles.wrapper}>
      <div sx={styles.galleryContainer(smallHeight && !scrollIsBeginning)}>
        {'gallery' in showroom && <ShowroomGallery gallery={showroom.gallery as Array<IPointOfPresenceGalleryItem>} />}
      </div>
      <div sx={styles.container(!smallHeight || scrollIsBeginning)}>
        {activeSwiper && <Swiper direction="vertical" modules={[Scrollbar, Mousewheel, FreeMode]} scrollbar={{
        draggable: true,
        hide: false,
        dragSize: 80
      }} onSwiper={swiper => {
        swiperInstance.current = swiper;
      }} followFinger slidesPerView="auto" freeMode mousewheel onScroll={onScroll}>
            <SwiperSlide>
              <div sx={styles.item}>
                <div sx={styles.details}>
                  <p>{showroom?.address?.city}</p>
                  <Heading h={1} size={24} uppercase pb={8} mobile>
                    {getShowroomName(showroom, t)}
                  </Heading>
                  <span>{showroom?.name}</span>
                  <span>
                    {showroom?.address?.street}
                    <br />
                    {showroom?.address?.postalCode} {showroom?.address?.city} {showroom?.country?.name}
                  </span>
                  <span>{showroom?.openingHours?.description}</span>
                  {showroom?.contact?.emails && showroom.contact.emails.length > 0 && showroom.contact.emails.map(email => email && <div sx={styles.emailLink} key={`${email}-${Math.random()}`}>
                            <Link href={`mailto:${email}`}>
                              <span>{email}</span>
                            </Link>
                          </div>)}
                  {showroom?.contact?.phones && showroom.contact.phones.length > 0 && showroom.contact.phones.map(phone => {
                if (!phone) return null;
                return <div sx={styles.phoneLink} key={`${phone}-${Math.random()}`}>
                          <Link href={`tel:${phone}`}>{phone.toUpperCase()}</Link>
                        </div>;
              })}
                </div>
                <div sx={styles.buttonsContainer}>
                  {showroom?.contact?.phones[0] && <Button variant="outlined" variantColor="dark" size="small" onClick={onClickCall}>
                      {t('button.call')}
                    </Button>}
                  {navigationPossible(showroom?.location) && (getNavigateUrl(showroom?.location, userLocation?.location ?? null) ? <Button variant="outlined" variantColor="dark" size="small" href={getNavigateUrl(showroom?.location, userLocation?.location ?? null) ?? ''} openInNewTab>
                        {t('button.navigate')}
                      </Button> : <Button variant="outlined" variantColor="dark" size="small" onClick={() => onClickNavigate(showroom.location, userLocation?.location)}>
                        {t('button.navigate')}
                      </Button>)}
                  {!navigationPossible(showroom?.location) && reviewBoosterURL && renderReviewBoosterURL()}
                </div>
                {navigationPossible(showroom?.location) && reviewBoosterURL && <div sx={styles.buttonsContainer}>{renderReviewBoosterURL()}</div>}
                <div sx={styles.description}>
                  <span dangerouslySetInnerHTML={getDescriptionSummary()} />
                </div>
              </div>
            </SwiperSlide>
          </Swiper>}
      </div>
      {showroom && showroom?.group?.code && showroom?.name && showroom?.ids && typeof showroom?.ids['INTERNAL-PIM-ID'] === 'string' && <div sx={styles.buttonContainer}>
            <Button variant="default" variantColor="dark" size="big" href={prepareShowroomUrl(showroom?.group?.code, showroom?.name, getPimInternalId(showroom), t)} onClick={() => handleShowroomPage()}>
              {t('button.goToTheShowroomPage')}
            </Button>
          </div>}
    </div>;
};
export default ShowroomInfo;